// Battle summary screen - shows division standings after a battle

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getTeamsInDivision } from '../systems/GameState';
import { GAME_CONSTANTS } from '../data/constants';

export class BattleSummary implements Screen {
  readonly state = ScreenState.BATTLE_SUMMARY;
  private context: ScreenContext;
  private gameState: GameState | null = null;

  constructor(context: ScreenContext) {
    this.context = context;
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
  }

  enter(): void {
    console.log('Entering BattleSummary');
  }

  exit(): void {
    console.log('Exiting BattleSummary');
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.MAIN_BACKGROUND);
      return;
    }

    canvas.clear('#1a1a3a'); // Dark blue background

    // Header
    this.renderHeader(canvas);

    // Division standings in a 2x2 grid
    this.renderDivisionStandings(canvas);

    // Continue button
    this.renderContinueButton(canvas);
  }

  private renderHeader(canvas: Canvas): void {
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 50, '#2a2a5a');
    canvas.drawText('Sarjataulukot', CONSTANTS.CANVAS_WIDTH / 2, 12, CONSTANTS.BIG_FONT, '#ffffff', 'center');

    const monthName = GAME_CONSTANTS.MONTHS[this.gameState!.month];
    canvas.drawText(
      `${monthName} ${this.gameState!.year} ${GAME_CONSTANTS.YEAR_SUFFIX}`,
      CONSTANTS.CANVAS_WIDTH / 2,
      32,
      CONSTANTS.MAIN_FONT,
      '#aaaaff',
      'center'
    );
  }

  private renderDivisionStandings(canvas: Canvas): void {
    const startX = 30;
    const startY = 70;
    const tableWidth = 470;
    const tableHeight = 280;
    const gap = 20;

    // Render each division in a 2x2 grid
    for (let div = 0; div < 4; div++) {
      const col = div % 2;
      const row = Math.floor(div / 2);
      const x = startX + col * (tableWidth + gap);
      const y = startY + row * (tableHeight + gap);

      this.renderDivisionTable(canvas, div, x, y, tableWidth, tableHeight);
    }
  }

  private renderDivisionTable(canvas: Canvas, divIndex: number, x: number, y: number, width: number, height: number): void {
    // Background
    canvas.drawRect(x, y, width, height, '#0a0a2a');
    canvas.drawRectOutline(x, y, width, height, '#4a4a8a');

    // Header
    canvas.drawRect(x, y, width, 30, '#3a3a6a');
    canvas.drawText(`Divisioona ${divIndex + 1}`, x + 10, y + 7, CONSTANTS.GLADIATOR_NAME_FONT, '#ffffff');

    // Column headers
    const headerY = y + 35;
    canvas.drawText('Joukkue', x + 10, headerY, CONSTANTS.MAIN_FONT, '#aaaaaa');
    canvas.drawText('V', x + width - 100, headerY, CONSTANTS.MAIN_FONT, '#aaaaaa');
    canvas.drawText('H', x + width - 60, headerY, CONSTANTS.MAIN_FONT, '#aaaaaa');
    canvas.drawText('Pts', x + width - 30, headerY, CONSTANTS.MAIN_FONT, '#aaaaaa');

    // Get teams sorted by wins
    const teams = getTeamsInDivision(this.gameState!, divIndex)
      .sort((a, b) => b.wins - a.wins || a.losses - b.losses);

    const rowHeight = 50;
    const dataStartY = y + 55;

    for (let i = 0; i < teams.length; i++) {
      const team = teams[i];
      const rowY = dataStartY + i * rowHeight;

      // Highlight player teams
      const isPlayerTeam = this.gameState!.playerTeamIds.includes(team.id);
      if (isPlayerTeam) {
        canvas.drawRect(x + 2, rowY, width - 4, rowHeight - 2, '#2a2a4a');
      }

      // Position indicator (1st, 2nd, etc.)
      const posColor = i === 0 ? '#ffd700' : (i === teams.length - 1 ? '#ff6666' : '#ffffff');
      canvas.drawText(`${i + 1}.`, x + 10, rowY + 15, CONSTANTS.MAIN_FONT, posColor);

      // Team name
      const nameColor = isPlayerTeam ? '#88ff88' : '#ffffff';
      canvas.drawText(team.name, x + 30, rowY + 15, CONSTANTS.MAIN_FONT, nameColor);

      // Wins
      canvas.drawText(`${team.wins}`, x + width - 100, rowY + 15, CONSTANTS.MAIN_FONT, '#88ff88');

      // Losses
      canvas.drawText(`${team.losses}`, x + width - 60, rowY + 15, CONSTANTS.MAIN_FONT, '#ff8888');

      // Points (wins * 2)
      const points = team.wins * 2;
      canvas.drawText(`${points}`, x + width - 30, rowY + 15, CONSTANTS.MAIN_FONT, '#ffff88');
    }
  }

  private renderContinueButton(canvas: Canvas): void {
    canvas.drawButton(
      'continue',
      'Jatka',
      CONSTANTS.CANVAS_WIDTH / 2 - 60,
      CONSTANTS.CANVAS_HEIGHT - 55,
      120,
      40,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#4a4a8a',
      () => this.context.onScreenChange(ScreenState.KILL_LEADERBOARD)
    );
  }

  handleClick(x: number, y: number): void {
    console.log(`BattleSummary click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (key === 'Enter' || key === ' ' || key === 'Escape') {
      this.context.onScreenChange(ScreenState.KILL_LEADERBOARD);
    }
  }
}
